import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Tabs,
  Tab,
  Snackbar,
  Alert,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DragHandleRoundedIcon from "@mui/icons-material/DragHandleRounded";

import Preview from "./Preview";
import StimRow from "./StimRow";

const DEFAULT_TABLE_HEIGHT = 150;
const MIN_TABLE_HEIGHT = 150;

const UploadStimDisplay = (props) => {
  const {
    selectedType,
    scriptData,
    vastUrl,
    file,
    preview,
    errors,
    isDragActive,
    getRootProps,
    getInputProps,
    handleTypeChange,
    setScriptData,
    setVastUrl,
    handleAddToUploadList,
    uploading,
    handleUpload,
    setFile,
    setPreview,
    uploadList = [],
    adAssetTypes,
    handleFieldChange,
    handleDelete,
    rowErrors,
    brands,
    languages,
    locations,
    brandCategories,
    isSuperUser,
    onCreateBrand,
    history,
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    setSnackbarOpen,
    applyToAll,
    setApplyToAll,
    handleApplyToAll,
  } = props;

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [tableHeight, setTableHeight] = useState(DEFAULT_TABLE_HEIGHT);

  const pageRef = useRef(null);
  const uploadButtonRef = useRef(null);

  const resizingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(DEFAULT_TABLE_HEIGHT);

  const getMaxTableHeight = () => {
    if (!pageRef.current || !uploadButtonRef.current) return DEFAULT_TABLE_HEIGHT;

    const pageRect = pageRef.current.getBoundingClientRect();
    const buttonRect = uploadButtonRef.current.getBoundingClientRect();

    const spaceAboveButton =
      buttonRect.top - pageRect.top - 20; // small breathing room

    return Math.max(MIN_TABLE_HEIGHT, Math.floor(spaceAboveButton));
  };

  const handleResizeStart = (e) => {
    e.preventDefault();
    resizingRef.current = true;
    startYRef.current = e.clientY;
    startHeightRef.current = tableHeight;

    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";

    window.addEventListener("mousemove", handleResizeMove);
    window.addEventListener("mouseup", handleResizeEnd);
  };

  const handleResizeMove = (e) => {
    if (!resizingRef.current) return;

    const delta = startYRef.current - e.clientY;
    const nextHeight = startHeightRef.current + delta;
    const maxHeight = getMaxTableHeight();

    setTableHeight(Math.max(MIN_TABLE_HEIGHT, Math.min(maxHeight, nextHeight)));
  };

  const handleResizeEnd = () => {
    resizingRef.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";

    window.removeEventListener("mousemove", handleResizeMove);
    window.removeEventListener("mouseup", handleResizeEnd);
  };

  useEffect(() => {
    const handleWindowResize = () => {
      const maxHeight = getMaxTableHeight();
      setTableHeight((prev) => Math.max(MIN_TABLE_HEIGHT, Math.min(maxHeight, prev)));
    };

    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
      window.removeEventListener("mousemove", handleResizeMove);
      window.removeEventListener("mouseup", handleResizeEnd);
    };
  }, []);

  return (
    <Box
      ref={pageRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        padding: "32px",
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {props.toggleItem && (
        <div style={{ position: "absolute", top: 177, left: 60, zIndex: 5 }}>
          <Tooltip title="Back to table">
            <IconButton onClick={props.toggleItem} aria-label="back" size="large">
              <ArrowBackRoundedIcon />
            </IconButton>
          </Tooltip>
        </div>
      )}

      <Box sx={{ flexShrink: 0 }}>
        <Tabs
          value={selectedType}
          onChange={(e, val) => handleTypeChange({ target: { value: val } })}
          centered
          textColor="primary"
          indicatorColor="primary"
          sx={{ mb: 3 }}
        >
          <Tab value="s" label="SCRIPT" />
          <Tab value="va" label="VAST URL/XML" />
          <Tab value="iv" label="IMAGE / VIDEO" />
        </Tabs>

        <div
          style={{
            minHeight: "250px",
            marginBottom: "20px",
            display: "flex",
            gap: "32px",
            alignItems: "flex-start",
          }}
        >
          <div style={{ width: "50%", display: "flex", flexDirection: "column" }}>
            <div style={{ height: "250px" }}>
              {selectedType === "s" && (
                <TextField
                  label="Script"
                  fullWidth
                  multiline
                  rows={10}
                  value={scriptData}
                  onChange={(e) => setScriptData(e.target.value)}
                  error={!!errors.scriptData}
                  style={{
                    height: "100%",
                    backgroundColor: "#f9f9f9",
                  }}
                  InputProps={{
                    style: {
                      height: "100%",
                      alignItems: "flex-start",
                    },
                  }}
                />
              )}

              {selectedType === "va" && (
                <TextField
                  label="VAST URL/XML"
                  fullWidth
                  multiline
                  rows={10}
                  value={vastUrl}
                  onChange={(e) => setVastUrl(e.target.value)}
                  error={!!errors.vastUrl}
                  style={{
                    height: "100%",
                    backgroundColor: "#f9f9f9",
                  }}
                  InputProps={{
                    style: {
                      height: "100%",
                      alignItems: "flex-start",
                    },
                  }}
                />
              )}

              {selectedType === "iv" && (
                <div
                  {...getRootProps()}
                  style={{
                    border: `2px dashed ${errors.file ? "#d32f2f" : "#ccc"}`,
                    height: "100%",
                    padding: "16px",
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: "#f9f9f9",
                    borderRadius: "4px",
                    boxSizing: "border-box",
                    overflowY: "auto",
                  }}
                >
                  <input {...getInputProps({ multiple: true })} />
                  {isDragActive ? (
                    <p>Drop the files here...</p>
                  ) : file.length > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <p
                        style={{
                          marginBottom: "10px",
                          fontWeight: "bold",
                          color: "#00badb",
                        }}
                      >
                        Files:
                      </p>

                      {file.map((f, i) => (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "90%",
                            backgroundColor: "#fff",
                            border: "1px solid #eee",
                            borderRadius: "6px",
                            padding: "5px 10px",
                            marginBottom: "8px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {f.name}
                          </span>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFile((prev) => prev.filter((_, idx) => idx !== i));
                              setPreview((prev) => prev.filter((_, idx) => idx !== i));
                            }}
                            style={{
                              background: "transparent",
                              border: "none",
                              color: "red",
                              fontWeight: "bold",
                              fontSize: "16px",
                              cursor: "pointer",
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>Drag & Drop your images or videos here, or click to browse</p>
                  )}
                </div>
              )}
            </div>
          </div>

<div
  style={{
    width: "50%",
    height: "250px",
    minHeight: "250px",
    maxHeight: "250px",
    border: "1px solid #ccc",
    backgroundColor: "#f9f9f9",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    position: "relative",
  }}
>
  <div
    style={{
      flex: 1,
      minHeight: 0,
      maxHeight: "100%",
      overflow: "hidden",
      position: "relative",
    }}
  >
    <Preview
      selectedType={selectedType}
      scriptData={scriptData}
      vastUrl={vastUrl}
      preview={preview}
      file={file}
    />
  </div>
</div>
        </div>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <Button
            onClick={handleAddToUploadList}
            disabled={
              (selectedType === "s" && !scriptData.trim()) ||
              (selectedType === "va" && !vastUrl.trim()) ||
              (selectedType === "iv" && file.length === 0)
            }
            style={{
              marginTop: "10px",
              padding: "6px 16px",
              backgroundColor: "#00badb",
              color: "#fff",
              fontSize: "10px",
              whiteSpace: "nowrap",
              borderRadius: "5px",
              cursor: "pointer",
              width: "140px",
              opacity:
                (selectedType === "s" && !scriptData.trim()) ||
                (selectedType === "va" && !vastUrl.trim()) ||
                (selectedType === "iv" && file.length === 0)
                  ? 0.6
                  : 1,
            }}
          >
            Add to upload list
          </Button>
        </div>

        {errors?.global && (
          <p style={{ color: "red", marginTop: "10px" }}>{errors.global}</p>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          mt: 3,
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: `${tableHeight + 18}px`,
            display: "flex",
            flexDirection: "column",
            pointerEvents: "auto",
          }}
        >
          <Box
            onMouseDown={handleResizeStart}
            sx={{
              height: "18px",
              cursor: "ns-resize",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
              border: "1px solid #e5e5e5",
              borderBottom: "none",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
              flexShrink: 0,
              "&:hover": {
                backgroundColor: "#fafafa",
              },
            }}
          >
            <DragHandleRoundedIcon sx={{ fontSize: 18, color: "#999" }} />
          </Box>

          <Box
            sx={{
              height: `${tableHeight}px`,
              overflow: "hidden",
              border: "1px solid #e5e5e5",
              borderTop: "none",
              borderBottomLeftRadius: "8px",
              borderBottomRightRadius: "8px",
              backgroundColor: "#fff",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                   "42px minmax(90px,0.9fr) minmax(100px,1fr) minmax(100px,1fr) minmax(90px,0.9fr) minmax(115px,1fr) minmax(110px,1fr) minmax(110px,1fr) 70px 34px",
                gap: 0.75,
                alignItems: "center",
                px: 0.75,
                py: 1,
                borderBottom: "1px solid #e5e5e5",
                backgroundColor: "#fff",
                fontWeight: 700,
                color: "#333",
                fontSize: "0.68rem",
                flexShrink: 0,
              }}
            >
              <div>PREVIEW</div>
              <div>NAME *</div>
              <div>ASSET TYPE *</div>
              <div>TYPE GROUP *</div>
              <div>TYPE *</div>
              <div>BRAND *</div>
              <div>LANGUAGE *</div>
              <div>COUNTRY *</div>
              <div>CLUTTER</div>
              <div />
            </Box>

            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              {uploadList.map((stim, index) => (
                <StimRow
                  key={index}
                  index={index}
                  stim={stim}
                  adAssetTypes={adAssetTypes}
                  adTypeGroups={stim.adTypeGroups || []}
                  adTypes={stim.adTypes || []}
                  brands={brands}
                  languages={languages}
                  locations={locations}
                  brandCategories={brandCategories}
                  isSuperUser={isSuperUser}
                  onFieldChange={handleFieldChange}
                  onDelete={handleDelete}
                  onCreateBrand={onCreateBrand}
                  errors={rowErrors[index] || {}}
                  applyToAll={applyToAll}
                  setApplyToAll={setApplyToAll}
                  handleApplyToAll={handleApplyToAll}
                  uploadListLength={uploadList.length}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      <div
        ref={uploadButtonRef}
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20px",
          flexShrink: 0,
        }}
      >
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={
            uploading ||
            uploadList.length === 0 ||
            rowErrors.some((err) => !!Object.keys(err).length)
          }
          sx={{
            zIndex: 999,
            padding: "6px 16px",
            backgroundColor: "#00badb",
            color: "#fff",
            fontSize: "10px",
            whiteSpace: "nowrap",
            fontWeight: "bold",
            width: "140px",
            opacity:
              uploading ||
              uploadList.length === 0 ||
              rowErrors.some((err) => !!Object.keys(err).length)
                ? 0.6
                : 1,
            "&.Mui-disabled": {
              backgroundColor: "#00badb",
              color: "#fff",
            },
          }}
        >
          {uploading ? "Uploading..." : "Upload All Stims"}
        </Button>
      </div>

      <Dialog
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Preview Content</DialogTitle>
        <DialogContent style={{ textAlign: "center" }}>
          {selectedType === "iv" && Array.isArray(preview) && preview.length > 0 ? (
            preview.map((p, i) => (
              <div key={i} style={{ marginBottom: "20px" }}>
                {file[i]?.type?.startsWith("image") ? (
                  <img
                    src={p}
                    alt="preview"
                    style={{ maxWidth: "100%", maxHeight: "300px" }}
                  />
                ) : (
                  <video controls style={{ maxWidth: "100%", maxHeight: "300px" }}>
                    <source src={p} type={file[i]?.type || "video/mp4"} />
                  </video>
                )}
              </div>
            ))
          ) : (
            <Preview selectedType={selectedType} scriptData={scriptData} vastUrl={vastUrl} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="outlined"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setSnackbarOpen(false);
                if (snackbarMessage === "All stims uploaded successfully!") {
                  history.push("/stims");
                }
              }}
            >
              OK
            </Button>
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UploadStimDisplay;